import { useEffect, useRef, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { reservasApi, historicoMesasApi } from '../services/api';
import { useToast } from '../hooks/useToast.tsx';
import { signalRService } from '../services/signalr';
import { ReservaModal } from '../components/ReservaModal';
import { MesaCard } from '../components/mapa/MesaCard';
import { ReservaMultiModal } from '../components/mapa/ReservaMultiModal';
import { OcupadaModal } from '../components/mapa/OcupadaModal';
import { OcuparMesaModal, type OcuparFormState } from '../components/mapa/OcuparMesaModal';
import { EditarOcupacaoModal } from '../components/mapa/EditarOcupacaoModal';
import { useLocaisComMesas, useMapaData, useMesasFiltradas } from '../hooks/useMapaData';
import { ESTADOS, toLocalDateTimeInput } from '../utils/mesaEstado';
import {
  buildInitialReservaForm,
  ensureClienteForReserva,
  validateReservaForm,
  type ReservaFormErrors,
} from '../utils/reservaForm';
import type { CreateReservaRequest, HistoricoMesa, MapaMesa, Reserva } from '../types';
import {
  Plus,
  Calendar,
  ChevronLeft,
  ChevronRight,
  Euro,
  MapPin,
  Search,
  Filter,
} from 'lucide-react';
import { format, addDays } from 'date-fns';
import { pt } from 'date-fns/locale';

export function MapaPage() {
  const { isAuthenticated } = useAuth();
  const [data, setData] = useState(() => new Date());
  const { success, error, warning } = useToast();
  const {
    mapaData,
    loading,
    clientes,
    mesas,
    locais,
    loadMapa,
    reloadDebounced,
    loadClientes,
    loadMesas,
    loadLocais,
  } = useMapaData(data);

  const [showNovaReserva, setShowNovaReserva] = useState(false);
  const [reservaEditando, setReservaEditando] = useState<Reserva | null>(null);
  const [mesaSelecionada, setMesaSelecionada] = useState<MapaMesa | null>(null);
  const [mesaOcupada, setMesaOcupada] = useState<MapaMesa | null>(null);
  const [historicoEditando, setHistoricoEditando] = useState<HistoricoMesa | null>(null);
  const [mesaHistoricoEditando, setMesaHistoricoEditando] = useState<MapaMesa | null>(null);
  const [formData, setFormData] = useState<CreateReservaRequest>(() => buildInitialReservaForm(new Date()));
  const [formErrors, setFormErrors] = useState<ReservaFormErrors>({});
  const [submitting, setSubmitting] = useState(false);
  const submittingRef = useRef(false);
  const [mesaParaOcupar, setMesaParaOcupar] = useState<MapaMesa | null>(null);
  const [ocuparForm, setOcuparForm] = useState<OcuparFormState>({
    numeroPessoas: 2,
    valor: 0,
    contatoReserva: '',
    notasMesa: '',
    clienteId: undefined,
  });
  const [ocuparSaving, setOcuparSaving] = useState(false);
  const [localFiltro, setLocalFiltro] = useState<number | null>(null);
  const [estadoFiltro, setEstadoFiltro] = useState<number | null>(null);
  const [mesaSearch, setMesaSearch] = useState('');

  // SignalR: reload com debounce quando outro browser/dispositivo altera dados
  useEffect(() => {
    if (!isAuthenticated) return;
    const handlers: Parameters<typeof signalRService.start>[0] = {
      onDataChanged: () => reloadDebounced(),
      onTableChanged: () => {
        loadMesas();
        loadLocais();
      },
      onHistoryCreated: () => reloadDebounced(),
      onHistoryClosed: () => reloadDebounced(),
      onHistoryDeleted: () => reloadDebounced(),
    };
    void signalRService.start(handlers);
    return () => {
      void signalRService.stop(handlers);
    };
  }, [isAuthenticated, reloadDebounced, loadMesas, loadLocais]);

  const mesasFiltradas = useMesasFiltradas(mapaData?.mesas, localFiltro, estadoFiltro, mesaSearch);
  const locaisComMesas = useLocaisComMesas(mesasFiltradas);

  const handleDataChange = (delta: number) => {
    setData((d) => addDays(d, delta));
  };

  const handleNovaReserva = (mesaId?: number) => {
    const mesa =
      mesaId ? (mapaData?.mesas.find((m) => m.id === mesaId) ?? mesas.find((m) => m.id === mesaId) ?? null) : null;
    const capacidade = mesa?.capacidadePessoas ?? 2;
    setFormData(buildInitialReservaForm(data, mesaId, capacidade));
    setFormErrors({});
    setReservaEditando(null);
    setShowNovaReserva(true);
  };

  const handleOcupar = (mesaId: number) => {
    const mesa = mapaData?.mesas.find((m) => m.id === mesaId) ?? null;
    if (!mesa) return;
    setMesaParaOcupar(mesa);
    setOcuparForm({
      numeroPessoas: mesa.capacidadePessoas || 2,
      valor: 0,
      contatoReserva: '',
      notasMesa: '',
      clienteId: undefined,
    });
  };

  const handleOcuparSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!mesaParaOcupar) return;
    if (!ocuparForm.numeroPessoas || ocuparForm.numeroPessoas < 1) {
      error('Nº pessoas obrigatório');
      return;
    }
    setOcuparSaving(true);
    try {
      const res = await historicoMesasApi.create({
        origem: 1, // Ocupacao walk-in — registada como paga; libertar encerra sem apagar
        dataMesa: toLocalDateTimeInput(new Date()),
        numeroPessoas: ocuparForm.numeroPessoas,
        valor: Number(ocuparForm.valor) || 0,
        pago: true,
        contatoReserva: ocuparForm.contatoReserva || undefined,
        notasMesa: ocuparForm.notasMesa || undefined,
        clienteId: ocuparForm.clienteId,
        mesaId: mesaParaOcupar.id,
      });
      if (res.success) {
        success('Mesa ocupada — valor registado');
        setMesaParaOcupar(null);
        loadMapa(true);
      } else error(res.message || 'Erro');
    } catch (e: unknown) {
      error(e instanceof Error ? e.message : 'Erro');
    } finally {
      setOcuparSaving(false);
    }
  };

  const handleEditarHistorico = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!historicoEditando) return;
    try {
      const res = await historicoMesasApi.update(historicoEditando.id, {
        ...historicoEditando,
        origem: historicoEditando.origem,
        dataMesa: historicoEditando.dataMesa,
        numeroPessoas: historicoEditando.numeroPessoas,
        valor: historicoEditando.valor,
        pago: historicoEditando.pago,
        contatoReserva: historicoEditando.contatoReserva,
        notasMesa: historicoEditando.notasMesa,
        clienteId: historicoEditando.clienteId,
        mesaId: historicoEditando.mesaId,
        reservaId: historicoEditando.reservaId,
        versao: historicoEditando.versao || 1,
      });
      if (res.success) {
        success('Ocupação atualizada');
        setHistoricoEditando(null);
        setMesaHistoricoEditando(null);
        loadMapa(true);
      } else {
        error(res.message || 'Erro ao atualizar');
      }
    } catch (e: unknown) {
      error(e instanceof Error ? e.message : 'Erro');
    }
  };

  const handleMesaClick = (mesa: MapaMesa) => {
    if (!mesa.podeReservar) {
      warning('Mesa desabilitada — intervalo removido, não pode receber reservas');
      return;
    }
    // Histórico não encerrado = mesa ocupada (o backend só devolve abertos em ultimoHistorico)
    const temHistoricoAberto = !!mesa.ultimoHistorico;
    if (temHistoricoAberto) {
      setMesaOcupada(mesa);
      return;
    }
    const reservasList = mesa.reservasAtivas;
    const hasMultiple = reservasList && reservasList.length > 1;
    if (hasMultiple || (mesa.reservaAtiva && reservasList && reservasList.length > 0)) {
      // múltiplas reservas — mostra lista
      setMesaSelecionada(mesa);
      return;
    }
    if (mesa.estado === 0) {
      handleNovaReserva(mesa.id);
    } else if (mesa.reservaAtiva) {
      setMesaSelecionada(mesa);
      setReservaEditando(mesa.reservaAtiva);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const errors = validateReservaForm(formData);
    setFormErrors(errors);
    if (Object.keys(errors).length > 0) return;
    if (submittingRef.current) return; // prevent double submission (ref avoids async state race)
    submittingRef.current = true;
    setSubmitting(true);
    try {
      const clienteId = await ensureClienteForReserva({
        formData,
        clientes,
        onClientesChanged: loadClientes,
      });
      // Mantém clienteId resolvido no form para retry não duplicar ficha
      if (clienteId !== formData.clienteId) {
        setFormData((prev) => ({ ...prev, clienteId }));
      }

      if (reservaEditando) {
        const response = await reservasApi.update(reservaEditando.id, {
          dataDaReserva: formData.dataDaReserva,
          numeroPessoas: formData.numeroPessoas,
          contatoReserva: formData.contatoReserva,
          notasReserva: formData.notasReserva,
          nomeCliente: formData.nomeCliente || undefined,
          clienteId,
          mesaId: formData.mesaId,
          valor: formData.valor,
          canalId: formData.canalId,
          versao: reservaEditando.versao,
        });
        if (response.success) {
          success('Reserva atualizada com sucesso');
          setShowNovaReserva(false);
          setReservaEditando(null);
          loadMapa(true);
        } else {
          error(response.message || 'Erro ao atualizar reserva');
        }
        return;
      }

      const request: CreateReservaRequest = {
        dataDaReserva: formData.dataDaReserva,
        numeroPessoas: formData.numeroPessoas,
        contatoReserva: formData.contatoReserva,
        notasReserva: formData.notasReserva,
        nomeCliente: formData.nomeCliente || undefined,
        clienteId,
        mesaId: formData.mesaId,
        valor: formData.valor,
        canalId: formData.canalId,
      };
      const response = await reservasApi.create(request);
      if (response.success) {
        success(clienteId !== formData.clienteId ? 'Cliente criado e reserva guardada' : 'Reserva criada com sucesso');
        setShowNovaReserva(false);
        setReservaEditando(null);
        loadMapa(true);
      } else {
        error(response.message || 'Erro ao criar reserva');
      }
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Erro ao criar reserva';
      error(message);
    } finally {
      submittingRef.current = false;
      setSubmitting(false);
    }
  };

  const handleConfirmar = async (reserva: Reserva) => {
    if (!confirm('Confirmar esta reserva?')) return;
    try {
      const response = await reservasApi.confirmar(reserva.id, { versao: reserva.versao });
      if (response.success) {
        success('Reserva confirmada');
        setMesaSelecionada(null);
        setMesaOcupada(null);
        setShowNovaReserva(false);
        setReservaEditando(null);
        loadMapa(true);
      } else {
        error(response.message || 'Erro ao confirmar');
      }
    } catch {
      error('Erro ao confirmar reserva');
    }
  };

  const handleCancelar = async (reserva: Reserva) => {
    if (!confirm('Tem a certeza que deseja cancelar esta reserva?')) return;
    try {
      const response = await reservasApi.cancelar(reserva.id, { versao: reserva.versao });
      if (response.success) {
        success('Reserva cancelada');
        setMesaSelecionada(null);
        setMesaOcupada(null);
        setShowNovaReserva(false);
        setReservaEditando(null);
        loadMapa(true);
      } else {
        error(response.message || 'Erro ao cancelar');
      }
    } catch {
      error('Erro ao cancelar reserva');
    }
  };

  const handleEditar = (reserva: Reserva) => {
    setFormData({
      dataDaReserva: reserva.dataDaReserva,
      numeroPessoas: reserva.numeroPessoas,
      contatoReserva: reserva.contatoReserva || '',
      notasReserva: reserva.notasReserva || '',
      nomeCliente: reserva.nomeCliente || '',
      clienteId: reserva.clienteId,
      mesaId: reserva.mesaId,
      valor: reserva.valor,
      canalId: reserva.canalId,
    });
    setReservaEditando(reserva);
    setMesaSelecionada(null);
    setMesaOcupada(null);
    setShowNovaReserva(true);
  };

  const handleClienteSaiu = async (mesa: MapaMesa) => {
    const h = mesa.ultimoHistorico;
    if (!h) return;
    if (!confirm(`Registar saída da Mesa ${mesa.numero}?`)) return;
    try {
      // Fecha a ocupação sem apagar: fica no histórico e conta como pago
      await historicoMesasApi.libertar(h.id);
      success('Saída registada — mesa livre, valor contabilizado como pago.');
      setMesaOcupada(null);
      loadMapa(true);
    } catch {
      error('Erro ao registar saída');
    }
  };

  if (loading) {
    return (
      <div className="page-loading">
        <div className="spinner"></div>
        <p>A carregar mapa...</p>
      </div>
    );
  }

  return (
    <div className="mapa-page">
      <div className="page-header">
        <div className="header-left">
          <h1 className="page-title">Mapa do Salão</h1>
          <div className="date-nav">
            <button className="btn btn-ghost btn-sm" onClick={() => handleDataChange(-1)} aria-label="Dia anterior">
              <ChevronLeft className="w-4 h-4" />
            </button>
            <span className="current-date">
              <Calendar className="w-4 h-4" aria-hidden="true" />
              {format(data, 'EEEE, d MMMM yyyy', { locale: pt })}
            </span>
            <button className="btn btn-ghost btn-sm" onClick={() => handleDataChange(1)} aria-label="Dia seguinte">
              <ChevronRight className="w-4 h-4" />
            </button>
            <button className="btn btn-secondary btn-sm" onClick={() => setData(new Date())}>Hoje</button>
          </div>
        </div>
        <div className="header-right">
          <button className="btn btn-primary" onClick={() => handleNovaReserva()} aria-label="Nova reserva">
            <Plus className="w-5 h-5" aria-hidden="true" />
            <span>Nova Reserva</span>
          </button>
        </div>
      </div>

      {mapaData?.billingPorLocal && mapaData.billingPorLocal.length > 0 && (
        <div className="card" style={{ padding: 12, marginBottom: 12, display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
          <strong style={{ display: 'flex', gap: 6, alignItems: 'center' }}><Euro className="w-4 h-4" /> Receita:</strong>
          {mapaData.billingPorLocal.map(b => (
            <span key={b.localId} style={{ background: 'var(--c-surface-2)', padding: '6px 10px', borderRadius: 8, fontSize: 12, border: '1px solid var(--c-border)' }}>
              <span style={{ fontWeight: 600 }}>{b.localDescricao}</span>: Previsto €{b.previsto.toFixed(2)} · Fechado €{b.fechado.toFixed(2)}
            </span>
          ))}
          <span style={{ marginLeft: 'auto', fontWeight: 700, fontSize: 13 }}>
            Total Previsto €{mapaData.billingPorLocal.reduce((a, b) => a + b.previsto, 0).toFixed(2)} · Fechado €{mapaData.billingPorLocal.reduce((a, b) => a + b.fechado, 0).toFixed(2)}
          </span>
        </div>
      )}

      {/* Filtros: local + estado + pesquisa */}
      <div className="mapa-filters">
        <div className="filter-group">
          <Filter className="w-4 h-4" />
          <select className="input input-sm" value={localFiltro ?? ''} onChange={e => setLocalFiltro(e.target.value ? Number(e.target.value) : null)}>
            <option value="">Todos os locais</option>
            {locais.map(l => (
              <option key={l.id} value={l.id}>{l.descricao}</option>
            ))}
          </select>
        </div>
        <div className="estado-pills">
          {ESTADOS.map(s => (
            <button
              key={String(s.id)}
              className={`pill ${estadoFiltro === s.id ? 'active' : ''}`}
              onClick={() => setEstadoFiltro(s.id)}
            >
              {s.label}
            </button>
          ))}
        </div>
        <div className="search-bar" style={{ maxWidth: 240, borderBottom: 'none', padding: 0 }}>
          <Search className="w-4 h-4" />
          <input type="text" placeholder="Nº mesa ou descrição..." value={mesaSearch} onChange={e => setMesaSearch(e.target.value)} className="input input-sm" />
        </div>
      </div>

      <div className="mapa-container">
        {locaisComMesas.length === 0 ? (
          <div className="empty-state">
            <MapPin className="w-12 h-12 text-gray-300" aria-hidden="true" />
            <h3>Nenhuma mesa para este filtro</h3>
            <p>Tente outro local ou estado</p>
            <button className="btn btn-secondary mt-4" onClick={() => { setLocalFiltro(null); setEstadoFiltro(null); setMesaSearch(''); }}>Limpar filtros</button>
          </div>
        ) : (
          <>
            {locaisComMesas.map(local => (
              <section key={local.localId} className="local-section" style={{ '--local-color': local.localCor } as React.CSSProperties}>
                <header className="local-header">
                  <div className="local-color-indicator" style={{ backgroundColor: local.localCor }} />
                  <h2 className="local-title">{local.localDescricao}</h2>
                  <span className="local-count">{local.mesas.filter(m => m.estado === 0 && m.podeReservar).length} livres de {local.mesas.length}</span>
                </header>
                <div className="mesas-grid">
                  {local.mesas.map(mesa => (
                    <MesaCard
                      key={mesa.id}
                      mesa={mesa}
                      onClick={() => handleMesaClick(mesa)}
                      onNovaReserva={handleNovaReserva}
                      onOcupar={handleOcupar}
                      onEditar={handleEditar}
                    />
                  ))}
                </div>
              </section>
            ))}
          </>
        )}
      </div>

      {showNovaReserva && (
        <ReservaModal
          title={reservaEditando ? 'Editar Reserva' : 'Nova Reserva'}
          formData={formData}
          formErrors={formErrors}
          clientes={clientes}
          mesas={mesas}
          onClose={() => { setShowNovaReserva(false); setReservaEditando(null); }}
          onSubmit={handleSubmit}
          onChange={setFormData}
          loading={submitting}
          reservaEditando={reservaEditando}
          onConfirmar={reservaEditando ? handleConfirmar : undefined}
          onCancelar={reservaEditando ? handleCancelar : undefined}
          onEditar={reservaEditando ? handleEditar : undefined}
        />
      )}

      {mesaSelecionada && (
        <ReservaMultiModal
          mesa={mesaSelecionada}
          onClose={() => { setMesaSelecionada(null); setReservaEditando(null); }}
          onConfirmar={handleConfirmar}
          onCancelar={handleCancelar}
          onEditar={(r) => { setMesaSelecionada(null); handleEditar(r); }}
        />
      )}

      {mesaOcupada && mesaOcupada.ultimoHistorico && (
        <OcupadaModal
          mesa={mesaOcupada}
          onClose={() => { setMesaOcupada(null); setHistoricoEditando(null); setMesaHistoricoEditando(null); }}
          onSaiu={() => handleClienteSaiu(mesaOcupada)}
          onEditar={(h) => { setHistoricoEditando(h); setMesaHistoricoEditando(mesaOcupada); setMesaOcupada(null); }}
        />
      )}

      {mesaParaOcupar && (
        <OcuparMesaModal
          mesa={mesaParaOcupar}
          form={ocuparForm}
          saving={ocuparSaving}
          onChange={(patch) => setOcuparForm((p) => ({ ...p, ...patch }))}
          onClose={() => setMesaParaOcupar(null)}
          onSubmit={handleOcuparSubmit}
        />
      )}

      {historicoEditando && (
        <EditarOcupacaoModal
          mesa={mesaHistoricoEditando}
          historico={historicoEditando}
          onChange={setHistoricoEditando}
          onClose={() => { setHistoricoEditando(null); setMesaHistoricoEditando(null); }}
          onSubmit={handleEditarHistorico}
        />
      )}
    </div>
  );
}
